import React, { FC, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { format } from 'date-fns';
import { diffLines } from 'diff';
import cx from 'classnames';
import { Sparkles } from '../../../../../../../redesign/javascript/components/Sparkles';
import { getLangKey } from '../../../../../models/Language';
import * as translations from '../strings';
import { ActivityStreamEntry, ChangeType } from './buildCommentStream';
import styles from './ActivityEntry.module.css';

const strings = translations[getLangKey()];

type Props = {
  entry: ActivityStreamEntry;
  showDescriptionChanges: boolean;
  getContainer: () => HTMLElement;
};

const SuggestedByAi: FC = () => (
  <span className={styles.suggestedByAi}>
    <Sparkles className={styles.sparklesIcon} linearGradient size={14} />
    <span>{strings.suggestedByAi}</span>
  </span>
);

const ActivityEntry: FC<Props> = ({
  entry,
  showDescriptionChanges,
  getContainer
}) => {
  const [showDiff, setShowDiff] = useState(false);
  const date = new Date(entry.timestamp);
  const descriptionDiff = showDescriptionChanges ? entry.descriptionDiff : null;
  const diff = descriptionDiff
    ? diffLines(descriptionDiff.previous, descriptionDiff.current)
    : [];

  return (
    <div>
      <p className={styles.sentence}>
        <span className={styles.actor}>{entry.actorName}</span>
        {entry.phrases.map((phrase, index) => (
          <React.Fragment key={`${phrase.type}-${index}`}>
            {[
              `${index > 0 ? ', ' : ' '}${phrase.lead}`,
              phrase.value ? (
                <span key="value" className={styles.value}>
                  {phrase.value}
                </span>
              ) : null,
              phrase.suggestedByAi ? <SuggestedByAi key="suggested" /> : null
            ]}
          </React.Fragment>
        ))}
        {' on '}
        <Tooltip
          title={format(date, "EEEE, MMMM d, yyyy 'at' h:mm a")}
          getPopupContainer={() => getContainer()}
        >
          <span className={styles.date} tabIndex={0}>
            {format(date, 'MMM d, yyyy')}
          </span>
        </Tooltip>
      </p>
      {descriptionDiff && (
        <>
          <Button
            type="link"
            size="small"
            className={styles.changesButton}
            onClick={() => setShowDiff(open => !open)}
          >
            {showDiff ? strings.hideChanges : strings.showChanges}
          </Button>
          {showDiff && (
            <div className={styles.diff}>
              {diff.map((part, index) => (
                <div
                  key={index}
                  className={cx({
                    [styles.diffAdded]: part.added,
                    [styles.diffRemoved]: part.removed
                  })}
                >
                  {part.value}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const activityIconType = (types: ChangeType[]): ChangeType | 'mixed' => {
  const unique = [...new Set(types)];
  if (unique.length !== 1) return 'mixed';
  return unique[0];
};

export default ActivityEntry;
